import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../config/firebase';

// ═══════════════════════════════════════════════════════════════
// ADMIN MFA CHALLENGE (Batch 16 M3)
// Shown after password login, before admin pages render.
// ═══════════════════════════════════════════════════════════════

export default function AdminMfaChallenge({ onSuccess }) {
  const [token, setToken] = useState('');
  const [isBackup, setIsBackup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async () => {
    setError('');
    setLoading(true);
    try {
      const callable = httpsCallable(functions, 'verifyMfaToken');
      await callable({ token: token.trim(), isBackupCode: isBackup });
      await auth.currentUser.getIdToken(true);
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(e.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-5">
      <div className="max-w-[400px] w-full p-7 rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_rgba(0,0,0,0.05)]">
        <h1 className="font-serif mt-0 mb-2 text-[1.5rem]">Verify your identity</h1>
        <p className="text-body text-[0.9rem] mb-5">
          {isBackup
            ? 'Enter one of your backup codes:'
            : 'Open your authenticator app and enter the 6-digit code:'}
        </p>

        <input
          type="text"
          inputMode={isBackup ? 'text' : 'numeric'}
          maxLength={isBackup ? 8 : 6}
          value={token}
          onChange={(e) => setToken(isBackup
            ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
            : e.target.value.replace(/\D/g, '')
          )}
          autoFocus
          placeholder={isBackup ? 'ABCD1234' : '123456'}
          className="w-full p-3.5 rounded-[10px] border border-slate-200 text-[1.3rem] text-center tracking-[6px] font-mono mb-3"
        />

        {error && <div className="p-2.5 rounded-lg bg-red-100 text-red-900 text-[0.85rem] mb-3">{error}</div>}

        <button onClick={handleVerify} disabled={loading || token.length < (isBackup ? 6 : 6)}
          className="w-full px-[18px] py-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.95rem] mb-3"
          style={{ opacity: token.length >= 6 ? 1 : 0.5 }}>
          {loading ? 'Verifying...' : 'Verify'}
        </button>

        <button onClick={() => { setIsBackup(!isBackup); setToken(''); setError(''); }}
          className="w-full p-2.5 rounded-lg border-0 bg-transparent text-brand-primary cursor-pointer font-semibold text-[0.85rem]">
          {isBackup ? 'Use authenticator code instead' : 'Use backup code instead'}
        </button>

        <div className="mt-4 pt-4 border-t border-slate-200 text-[0.8rem] text-muted text-center">
          Lost access to your codes? Contact a super-admin to reset.
        </div>
      </div>
    </div>
  );
}